import React, { useEffect, useState } from 'react';
import { useAuth } from '../constants/AuthContext';
import { getSummary, getSales } from '../constants/api';
import { COLORS } from '../constants/theme';
import { Eye, EyeOff } from 'lucide-react';

function StatCard({ icon, label, value, color, bg }) {
  return (
    <div style={{ background: 'white', borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 900, color: color ?? COLORS.ink }}>{value}</div>
        <div style={{ fontSize: 12, color: COLORS.inkLight, marginTop: 3 }}>{label}</div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'Owner';
  const isMobile = window.innerWidth < 768;
  const [summary, setSummary] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showValues, setShowValues] = useState(true);

  const fetchData = async () => {
    try {
      const [summaryRes, salesRes] = await Promise.all([getSummary(), getSales()]);
      setSummary(summaryRes.data);
      setRecentSales(salesRes.data.filter(s => !s.isVoided).slice(0, 5));
    } catch (err) {
      console.log('Dashboard error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fmt = (val) => showValues ? `₦${Number(val ?? 0).toLocaleString()}` : '••••••';

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: COLORS.accent, fontSize: 16 }}>
      Loading...
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 900, color: COLORS.ink }}>
            {user?.fullName} 👋
          </div>
          <div style={{ fontSize: 13, color: COLORS.inkLight, marginTop: 4 }}>
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
        <button
          onClick={() => setShowValues(v => !v)}
          style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {showValues ? <EyeOff size={16} color={COLORS.inkMid} /> : <Eye size={16} color={COLORS.inkMid} />}
        </button>
      </div>

      {/* Today's overview */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.inkLight, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 12 }}>
          Today's Overview
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
          <StatCard icon="💰" label="Today's Sales" value={fmt(summary?.todaySales)} color={COLORS.accent} bg={COLORS.accentLight} />
          {isOwner && <StatCard icon="📈" label="Today's Profit" value={fmt(summary?.todayProfit)} color="#C89B2A" bg="#FDF6E3" />}
          <StatCard icon="🛒" label="Orders" value={summary?.todayTransactions ?? 0} bg="#EEF2FF" />
          <StatCard icon="📦" label="Products" value={summary?.totalProducts ?? 0} bg={COLORS.accentLight} />
        </div>
      </div>

      {/* Recent sales */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.inkLight, letterSpacing: 1.5, textTransform: 'uppercase' }}>
            Recent Sales
          </div>
          <a href="/history" style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent, textDecoration: 'none' }}>View all →</a>
        </div>

        {recentSales.length === 0 ? (
          <div style={{ background: 'white', borderRadius: 16, padding: 40, border: `1px solid ${COLORS.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🧾</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.ink, marginBottom: 6 }}>No sales yet</div>
            <div style={{ fontSize: 13, color: COLORS.inkLight, marginBottom: 16 }}>Record your first sale to get started</div>
            <a href="/record" style={{ background: COLORS.accent, color: 'white', padding: '11px 20px', borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 700 }}>
              + Record Sale
            </a>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: 16, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
            {recentSales.map((sale, i) => (
              <div key={sale.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: i < recentSales.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{sale.productName}</div>
                  <div style={{ fontSize: 12, color: COLORS.inkLight, marginTop: 2 }}>
                    ×{sale.quantitySold} · {new Date(sale.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>
                    {showValues ? `₦${sale.totalAmount.toLocaleString()}` : '••••••'}
                  </div>
                  {isOwner && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginTop: 2 }}>
                      {showValues ? `+₦${sale.profit.toLocaleString()}` : '••••••'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}